import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { getSkill, updateSkill } from '../services/skillApi';
import { ArrowLeft, Loader, Save, Eye, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { splitSkillMarkdown, stringifySkillMarkdown } from '../utils/skillParser';
import { Button } from '../components/ui/Button';

export const SkillEdit = () => {
    const { t } = useTranslation(['pages-misc-2', 'common']);
    const navigate = useNavigate();
    const location = useLocation();
    const { databaseId, skillId } = useParams();
    const { showToast } = useToast();
    const returnFolder = location.state?.returnFolder ?? null;

    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const [name, setName] = useState('');
    const [userSlug, setUserSlug] = useState('');
    const [description, setDescription] = useState('');
    const [model, setModel] = useState('sonnet');
    const [skillMarkdown, setSkillMarkdown] = useState('');
    const [activeTab, setActiveTab] = useState('edit');

    const textareaRef = useRef(null);

    const applyLoadedSkill = useCallback((skill) => {
        if (!skill) return;
        const content = skill.file_content || '';
        setSkillMarkdown(content);
        const { frontmatter } = splitSkillMarkdown(content);
        setName(frontmatter.name || skill.name || '');
        setDescription(frontmatter.description || skill.description || '');
        setModel(frontmatter.model || skill.model || 'sonnet');
        setUserSlug(skill.slug || '');
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadSkill = async () => {
            setLoading(true);
            setSkillMarkdown('');
            setName('');
            setDescription('');
            setModel('sonnet');
            try {
                const resp = await getSkill(skillId);
                const skill = resp.data?.skill || resp.data;
                if (cancelled) return;
                if (skill) applyLoadedSkill(skill);
            } catch (e) {
                console.error('Failed to load skill:', e);
                if (!cancelled) showToast(t('pages-misc-2:skillEdit.failedToLoadSkill'), 'error');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        if (skillId) loadSkill();
        return () => {
            cancelled = true;
        };
    }, [skillId, showToast, applyLoadedSkill, t]);

    const onMarkdownChange = (e) => {
        const v = e.target.value;
        setSkillMarkdown(v);
        const { frontmatter } = splitSkillMarkdown(v);
        if (frontmatter.name !== undefined) setName(frontmatter.name);
        if (frontmatter.description !== undefined) setDescription(frontmatter.description);
        if (frontmatter.model !== undefined) setModel(frontmatter.model);
    };

    const onNameChange = (e) => {
        const v = e.target.value;
        setName(v);
        const { body } = splitSkillMarkdown(skillMarkdown);
        setSkillMarkdown(stringifySkillMarkdown({ name: v, description, model }, body));
    };

    const onDescriptionChange = (e) => {
        const v = e.target.value;
        setDescription(v);
        const { body } = splitSkillMarkdown(skillMarkdown);
        setSkillMarkdown(stringifySkillMarkdown({ name, description: v, model }, body));
    };

    const onModelChange = (e) => {
        const v = e.target.value;
        setModel(v);
        const { body } = splitSkillMarkdown(skillMarkdown);
        setSkillMarkdown(stringifySkillMarkdown({ name, description, model: v }, body));
    };

    const { body: previewBody } = splitSkillMarkdown(skillMarkdown);

    const skillsListPath = returnFolder
        ? `/skills/${databaseId}/folder/${encodeURIComponent(returnFolder)}`
        : `/skills/${databaseId}`;

    const handleBack = () => navigate(skillsListPath);

    const handleSave = async () => {
        if (!name.trim()) {
            showToast(t('pages-misc-2:skillCreate.skillNameRequired'), 'error');
            return;
        }
        if (!userSlug.trim()) {
            showToast(t('pages-misc-2:skillCreate.skillSlugRequired'), 'error');
            return;
        }

        setSaving(true);
        try {
            const { body } = splitSkillMarkdown(skillMarkdown);
            const payload = {
                name: name.trim(),
                skill_slug: userSlug.trim(),
                description: description.trim(),
                model: model || null,
                body: body.trimEnd() + '\n'
            };
            const resp = await updateSkill(skillId, payload);
            if (resp.data?.status === 'success' || resp.status === 200 || resp.data?.message) {
                showToast(t('pages-misc-2:skillEdit.skillUpdatedSuccess'), 'success');
                try {
                    const refreshed = await getSkill(skillId);
                    const skill = refreshed.data?.skill || refreshed.data;
                    if (skill) applyLoadedSkill(skill);
                } catch (refreshErr) {
                    console.error('Failed to refresh skill after save:', refreshErr);
                }
                return;
            }
            showToast(t('pages-misc-2:skillEdit.failedToUpdateSkill'), 'error');
        } catch (e) {
            console.error('Failed to update skill:', e);
            const msg =
                e.response?.data?.detail ||
                (Array.isArray(e.response?.data?.detail)
                    ? e.response.data.detail.map((d) => d.msg || d).join('; ')
                    : null) ||
                t('pages-misc-2:skillEdit.failedToUpdateSkill');
            showToast(typeof msg === 'string' ? msg : t('pages-misc-2:skillEdit.failedToUpdateSkill'), 'error');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [skillMarkdown, activeTab, loading]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50/50 flex items-center justify-center">
                <Loader className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="page-content pb-20">
            <div className="mb-6">
                <button
                    onClick={handleBack}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('common:back')}
                </button>

                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('pages-misc-2:skillEdit.editSkill')}</h1>
                        <p className="text-sm text-gray-400 mt-1 flex items-center font-medium">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 mr-2 shadow-sm shadow-primary-200"></span>
                            {t('pages-misc-2:skillEdit.markdownFirstSkillDefinition')}
                        </p>
                    </div>
                    <Button
                        variant="primary"
                        size="lg"
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="font-bold uppercase tracking-widest text-[11px] gap-2 shadow-md shadow-primary-100/80 hover:-translate-y-0.5 active:translate-y-0 disabled:hover:translate-y-0 disabled:shadow-none"
                    >
                        {saving ? (
                            <>
                                <Loader className="w-4 h-4 animate-spin shrink-0" />
                                <span>{t('common:saving')}</span>
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4 shrink-0" />
                                <span>{t('pages-misc-2:skillCreate.saveSkill')}</span>
                            </>
                        )}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('pages-misc-2:skillCreate.skillDetails')}</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('pages-misc-2:skillCreate.skillNameLabel')}
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={onNameChange}
                                    placeholder={t('pages-misc-2:skillCreate.skillNamePlaceholder')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('pages-misc-2:skillCreate.skillSlugLabel')}
                                </label>
                                <input
                                    type="text"
                                    value={userSlug}
                                    onChange={(e) => setUserSlug(e.target.value)}
                                    placeholder={t('pages-misc-2:skillEdit.skillSlugPlaceholder')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('common:description')}
                                </label>
                                <textarea
                                    value={description}
                                    onChange={onDescriptionChange}
                                    placeholder={t('pages-misc-2:skillCreate.descriptionPlaceholder')}
                                    rows={2}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('pages-misc-2:skillCreate.modelOptional')}
                                </label>
                                <select
                                    value={model}
                                    onChange={onModelChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                >
                                    <option value="sonnet">Sonnet</option>
                                    <option value="opus">Opus</option>
                                    <option value="">{t('pages-misc-2:skillCreate.defaultOption')}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="bg-primary-50 rounded-xl p-6 border border-primary-100">
                        <h3 className="text-sm font-semibold text-primary-900 mb-2 flex items-center">
                            <FileText className="w-4 h-4 mr-2" />
                            {t('pages-misc-2:skillCreate.skillTips')}
                        </h3>
                        <ul className="text-sm text-primary-700 space-y-1">
                            <li>• {t('pages-misc-2:skillEdit.tipSkillsLiveUnder')} <code className="bg-primary-100 px-1 rounded">skills/&lt;area&gt;/</code> {t('pages-misc-2:skillEdit.tipForPlanGeneration')}</li>
                            <li>• {t('pages-misc-2:skillCreate.tipEnhancePlans')}</li>
                            <li>• {t('pages-misc-2:skillCreate.tipIncludeSteps')}</li>
                            <li>• {t('pages-misc-2:skillEdit.tipUseMarkdownFormatting')}</li>
                        </ul>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="flex items-center border-b border-gray-200">
                            <button
                                type="button"
                                onClick={() => setActiveTab('edit')}
                                className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'edit'
                                    ? 'bg-white text-primary-600 border-b-2 border-primary-600'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <FileText className="w-4 h-4" />
                                    <span>{t('pages-misc-2:skillCreate.editMarkdown')}</span>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('preview')}
                                className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'preview'
                                    ? 'bg-white text-primary-600 border-b-2 border-primary-600'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <Eye className="w-4 h-4" />
                                    <span>{t('pages-misc-2:skillCreate.preview')}</span>
                                </div>
                            </button>
                        </div>

                        <div className="p-4">
                            {activeTab === 'edit' ? (
                                <textarea
                                    key={`md-${skillId}`}
                                    ref={textareaRef}
                                    value={skillMarkdown}
                                    onChange={onMarkdownChange}
                                    placeholder={t('pages-misc-2:skillCreate.enterMarkdownPlaceholder')}
                                    className="w-full min-h-[500px] font-mono text-sm border-0 focus:ring-0 resize-none outline-none"
                                    style={{ height: 'auto' }}
                                />
                            ) : (
                                <div className="prose prose-sm max-w-none min-h-[500px] overflow-auto">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {previewBody}
                                    </ReactMarkdown>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
